/**
 * Hydrate nodes that carry `product="..."` attributes with data from a
 * Shopify Storefront `Product`.
 *
 * Supported values (observed on lamaisonduroy.com):
 *   title | url | description | images | vendor | link | compareAtPrice
 *   min-price | max-price | type | tags | published-at | video
 *   external-video | models | specific-image
 *
 * Also handles `[data-prop="price|compareAtPrice"]` children inside
 * `<smootify-price>` wrappers.
 */

import { config } from "../config.js";
import { formatMoney } from "../ui/format.js";
import type { MoneyV2, Product, ShopifyImage } from "../types.js";

// ---------------------------------------------------------------------------
//  Events
// ---------------------------------------------------------------------------

export interface ChangePriceDetail {
  price: MoneyV2 | null;
  compareAtPrice: MoneyV2 | null;
  handle: string;
}

function dispatchChangePrice(target: EventTarget, detail: ChangePriceDetail): void {
  target.dispatchEvent(
    new CustomEvent<ChangePriceDetail>("changePrice", {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

// ---------------------------------------------------------------------------
//  Internals
// ---------------------------------------------------------------------------

function hasChildText(el: Element): boolean {
  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType === Node.TEXT_NODE && (n.textContent ?? "").trim().length > 0) {
      return true;
    }
  }
  return false;
}

function productUrl(handle: string): string {
  const base = config.productsBase.replace(/^\/+|\/+$/g, "");
  return `/${base}/${encodeURIComponent(handle)}`;
}

function priceRange(product: Product): { min: MoneyV2 | null; max: MoneyV2 | null } {
  if (product.variants.length === 0) return { min: null, max: null };
  let min: MoneyV2 = product.variants[0]!.price;
  let max: MoneyV2 = product.variants[0]!.price;
  for (const v of product.variants) {
    if (Number(v.price.amount) < Number(min.amount)) min = v.price;
    if (Number(v.price.amount) > Number(max.amount)) max = v.price;
  }
  return { min, max };
}

function firstCompareAt(product: Product): MoneyV2 | null {
  for (const v of product.variants) {
    if (v.compareAtPrice) return v.compareAtPrice;
  }
  return null;
}

function setText(el: Element, text: string): void {
  el.textContent = text;
}

function sanitizeHtml(raw: string): string {
  // Minimal sanitiser: strip <script> tags and on* inline handlers. The
  // Shopify admin API's descriptionHtml is user-controlled content from the
  // merchant, so we still run a cheap pass. Full DOMPurify integration is
  // out of scope for this module.
  if (!raw) return "";
  return raw
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "");
}

// ---------------------------------------------------------------------------
//  Template repetition helper
// ---------------------------------------------------------------------------

function repeatTemplate<T>(
  template: Element,
  items: T[],
  fill: (node: Element, item: T, index: number) => void,
): Element[] {
  const parent = template.parentElement;
  if (!parent) return [];

  // Siblings that were previously rendered for this template are marked
  // with `data-lmdr-rendered="true"` and removed on re-hydration.
  const rendered = parent.querySelectorAll(':scope > [data-lmdr-rendered="true"]');
  rendered.forEach((n) => n.remove());

  // Hide the original template so the user doesn't see the "empty" version.
  (template as HTMLElement).style.display = "none";
  template.setAttribute("data-lmdr-template", "true");

  const out: Element[] = [];
  for (let i = 0; i < items.length; i++) {
    const clone = template.cloneNode(true) as Element;
    clone.removeAttribute("data-lmdr-template");
    (clone as HTMLElement).style.removeProperty("display");
    clone.setAttribute("data-lmdr-rendered", "true");
    fill(clone, items[i]!, i);
    parent.insertBefore(clone, template);
    out.push(clone);
  }
  return out;
}

/**
 * Resize une URL Shopify CDN via le param `?width=` (Shopify reencode l image
 * a la taille demandee en WebP/AVIF si supporte par le navigateur). Reduit
 * le poids des images cards de ~80% (200KB → ~30-50KB).
 *
 * Skip si pas une URL Shopify CDN ou si width deja present.
 */
function shopifyResize(url: string, width: number): string {
  if (typeof url !== "string" || url.length === 0) return url;
  if (!/cdn\.shopify\.com/.test(url)) return url;
  if (/[?&]width=/.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}width=${width}`;
}

function applyImage(el: Element, image: ShopifyImage | null, opts: { width?: number } = {}): void {
  if (!image) return;
  const url = opts.width ? shopifyResize(image.url, opts.width) : image.url;
  if (el instanceof HTMLImageElement) {
    el.src = url;
    if (image.altText) el.alt = image.altText;
    // Voir variant.ts — retire les attrs HTML width/height pour laisser
    // le CSS (aspect-ratio, object-fit) piloter le sizing.
    el.removeAttribute("width");
    el.removeAttribute("height");
    // Lazy loading natif pour cards hors viewport
    if (!el.hasAttribute("loading")) el.setAttribute("loading", "lazy");
  } else if (el instanceof HTMLElement) {
    el.style.backgroundImage = `url(${JSON.stringify(url)})`;
  }
}

// ---------------------------------------------------------------------------
//  Data-prop handling (price children of <smootify-price>)
// ---------------------------------------------------------------------------

function hydrateDataProp(
  root: ParentNode,
  product: Product,
): void {
  const nodes = root.querySelectorAll("[data-prop]");
  const { min } = priceRange(product);
  const compareAt = firstCompareAt(product);

  nodes.forEach((n) => {
    const prop = n.getAttribute("data-prop");
    if (prop === "price" && min) {
      setText(n, formatMoney(min));
    } else if (prop === "compareAtPrice") {
      if (compareAt) {
        setText(n, formatMoney(compareAt));
        (n as HTMLElement).style.removeProperty("display");
      } else if (n instanceof HTMLElement) {
        n.style.display = "none";
      }
    }
  });
}

// ---------------------------------------------------------------------------
//  Public: hydrate a single product subtree
// ---------------------------------------------------------------------------

export interface HydrateProductOptions {
  /** Root element (usually a `<smootify-product>`) whose descendants get hydrated. */
  root: ParentNode;
  /** Product data from the Storefront API. */
  product: Product;
}

export function hydrateProduct({ root, product }: HydrateProductOptions): void {
  // `variation=` est un alias Smootify pour `product=` dans certains contextes
  // (related cards, last-viewed). On matche les 2 — `product=` prend priorité.
  const nodes = root.querySelectorAll<HTMLElement>("[product], [variation]");

  nodes.forEach((el) => {
    const kind = el.getAttribute("product") ?? el.getAttribute("variation") ?? "";
    switch (kind) {
      case "title":
        setText(el, product.title);
        break;

      case "description":
        el.innerHTML = sanitizeHtml(product.descriptionHtml);
        break;

      case "url":
      case "link": {
        const href = productUrl(product.handle);
        if (el instanceof HTMLAnchorElement) {
          el.href = href;
        } else {
          el.setAttribute("href", href);
        }
        if (!hasChildText(el) && el.children.length === 0) {
          setText(el, product.title);
        }
        break;
      }

      case "vendor":
        setText(el, product.vendor);
        break;

      case "type":
        setText(el, product.productType);
        break;

      case "images": {
        // Cas spécial : `product="images"` posé sur un slider Webflow (.w-slider).
        // Webflow CMS ne génère qu'UNE slide template ; on clone le 1er .w-slide
        // enfant pour chaque image produit, puis on demande à Webflow de
        // re-initialiser le slider (arrows, nav dots, autoplay).
        if (el.classList.contains("w-slider")) {
          hydrateWebflowSlider(el, product.images);
          break;
        }
        repeatTemplate(el, product.images, (clone, img) => {
          // If the template itself is an <img>, fill its attributes. Otherwise
          // look for a nested <img> or element with `product="specific-image"`.
          if (clone instanceof HTMLImageElement) {
            applyImage(clone, img);
            return;
          }
          const nestedImg = clone.querySelector("img");
          if (nestedImg) {
            applyImage(nestedImg, img);
          } else {
            applyImage(clone, img);
          }
        });
        break;
      }

      case "specific-image": {
        const raw = el.getAttribute("index");
        const idx = raw ? Number.parseInt(raw, 10) : 0;
        const image = product.images[Number.isFinite(idx) ? idx : 0] ?? null;
        applyImage(el, image);
        break;
      }

      case "tags": {
        repeatTemplate(el, product.tags, (clone, tag) => {
          setText(clone, tag);
        });
        break;
      }

      case "min-price": {
        const { min } = priceRange(product);
        if (min) {
          setText(el, formatMoney(min));
          dispatchChangePrice(el, {
            price: min,
            compareAtPrice: null,
            handle: product.handle,
          });
        }
        break;
      }

      case "max-price": {
        const { max } = priceRange(product);
        if (max) {
          setText(el, formatMoney(max));
          dispatchChangePrice(el, {
            price: max,
            compareAtPrice: null,
            handle: product.handle,
          });
        }
        break;
      }

      case "compareAtPrice": {
        const compareAt = firstCompareAt(product);
        if (compareAt) {
          setText(el, formatMoney(compareAt));
          el.style.removeProperty("display");
          dispatchChangePrice(el, {
            price: null,
            compareAtPrice: compareAt,
            handle: product.handle,
          });
        } else {
          el.style.display = "none";
        }
        break;
      }

      case "published-at":
        // No `publishedAt` on the Product shape yet; noop. Will be populated
        // if / when the fragment is extended.
        break;

      case "video":
      case "external-video":
      case "models":
        // Reserved for media extensions — leave the template alone so that
        // hand-authored fallbacks still render.
        break;

      default:
        // Unknown product="..." value; ignore.
        break;
    }
  });

  hydrateDataProp(root, product);
  hydrateVariantThumbs(root, product);
}

// ---------------------------------------------------------------------------
//  Variant thumbnails sur card (hover-to-swap)
// ---------------------------------------------------------------------------

/**
 * Injecte un bandeau de mini-thumbnails de variantes en bas de l'image de
 * card, avec hover-swap : le hover sur une mini change l'image principale.
 *
 * Activé sur :
 *   - Cards de collection (`<smootify-product data-id="gid://...">` sans slider)
 *   - Clones multi : related / last-viewed / wishlist / cart-upsells
 *
 * Désactivé sur :
 *   - Page produit principale (`.w-slider[product="images"]` présent — le
 *     user a déjà sa galerie + thumbnails)
 *   - Produits avec < 2 variantes distinctes par image
 *   - Devices sans hover (touch) via CSS `@media (hover: none)`
 */
function hydrateVariantThumbs(root: ParentNode, product: Product): void {
  if (!product.variants || product.variants.length < 2) return;

  // Skip si un slider Webflow est présent — c'est la page produit principale.
  if ((root as Element).querySelector?.('.w-slider[product="images"]')) return;

  // Collecte les variantes avec image DISTINCTE.
  const seen = new Set<string>();
  const withImage = product.variants.filter((v) => {
    const url = v.image?.url;
    if (!url) return false;
    if (seen.has(url)) return false;
    seen.add(url);
    return true;
  });
  if (withImage.length < 2) return;

  // Trouve l'image principale de la card (hors slider).
  const rootEl = root as Element;
  const candidates = Array.from(
    rootEl.querySelectorAll<HTMLElement>('[variation="image"], [product="image"]'),
  );
  const mainImg = candidates.find((el) => !el.closest(".w-slider"));
  if (!mainImg) return;

  // On wrappe l'image dans un `<div data-lmdr-img-wrap>` qui sert d'ancre
  // pour l'overlay (position:relative, overflow:hidden). Le wrap est créé
  // une seule fois — si re-hydratation on réutilise le wrap existant.
  let wrap: HTMLElement;
  const parent = mainImg.parentElement;
  if (!parent) return;
  if (parent.hasAttribute("data-lmdr-img-wrap")) {
    wrap = parent;
  } else {
    wrap = document.createElement("div");
    wrap.setAttribute("data-lmdr-img-wrap", "");
    // Préserve le layout original en insérant le wrap à la place de l'image.
    parent.insertBefore(wrap, mainImg);
    wrap.appendChild(mainImg);
  }

  // Retire un éventuel overlay précédent (cas re-hydratation).
  wrap.querySelector(":scope > [data-lmdr-variant-thumbs]")?.remove();

  // Mémorise l'image par défaut pour restaurer au mouseleave.
  //
  // CRITIQUE : on prend la featured image du PRODUIT, pas mainImg.src courant.
  // Raison : à ce stade, mainImg peut encore afficher le placeholder Webflow
  // (le swap vers la vraie image produit se fait via l'event changeVariant
  // qui fire async APRES hydrateVariantThumbs). Si on capture mainImg.src,
  // data-lmdr-default-img stocke l'URL du placeholder.svg et le mouseleave
  // casse la card en revenant sur ce placeholder (ou sur l'image d une autre
  // card si le clone reutilise un ancien wrap).
  //
  // Fallback sur mainImg.src uniquement si pas d image sur le produit.
  // product.images[0] = featured image (Storefront fragment ne renvoie pas
  // featuredImage separement mais le 1er element de images[] correspond).
  const defaultUrl = product.images[0]?.url ?? extractCurrentImageUrl(mainImg);
  if (defaultUrl) {
    wrap.setAttribute("data-lmdr-default-img", defaultUrl);
  }

  // Construit l'overlay (styling via critical-style.ts — pas de styles inline).
  const overlay = document.createElement("div");
  overlay.setAttribute("data-lmdr-variant-thumbs", "");

  const MAX_THUMBS = 6;
  withImage.slice(0, MAX_THUMBS).forEach((v, i) => {
    const thumb = document.createElement("img");
    // Variant thumb sur card = 36px display → resize 100 (Retina)
    thumb.src = shopifyResize(v.image!.url, 100);
    thumb.alt = v.title || "";
    thumb.loading = "lazy";
    thumb.setAttribute("data-lmdr-thumb-idx", String(i));
    // Pose un data-attribut pour le style .active via CSS.
    if (i === 0) thumb.setAttribute("data-lmdr-thumb-active", "1");
    // Fade l'image principale lors du swap pour un effet smooth.
    const activate = (): void => {
      fadeSwapMainImage(mainImg, shopifyResize(v.image!.url, 600));
      overlay
        .querySelectorAll<HTMLImageElement>("[data-lmdr-thumb-idx]")
        .forEach((t) =>
          t === thumb
            ? t.setAttribute("data-lmdr-thumb-active", "1")
            : t.removeAttribute("data-lmdr-thumb-active"),
        );
    };
    thumb.addEventListener("mouseenter", activate);
    // Click = sticky swap (ne revient pas au default au mouseleave) — utile
    // mobile + desktop.
    thumb.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      activate();
      // Déplace le default sur cette variante → mouseleave ne restore pas.
      wrap.setAttribute("data-lmdr-default-img", v.image!.url);
    });
    overlay.appendChild(thumb);
  });

  // Restore image default au mouseleave du wrapper entier.
  const restore = (): void => {
    const url = wrap.getAttribute("data-lmdr-default-img");
    if (url) fadeSwapMainImage(mainImg, url);
    overlay
      .querySelectorAll<HTMLImageElement>("[data-lmdr-thumb-idx]")
      .forEach((t, i) =>
        i === 0
          ? t.setAttribute("data-lmdr-thumb-active", "1")
          : t.removeAttribute("data-lmdr-thumb-active"),
      );
  };
  if (!wrap.hasAttribute("data-lmdr-thumbs-listener")) {
    wrap.setAttribute("data-lmdr-thumbs-listener", "true");
    wrap.addEventListener("mouseleave", restore);
  }

  wrap.appendChild(overlay);
}

/**
 * Swap avec petit fade pour éviter le "pop" visuel — opacity 0.5 pendant
 * le load de la nouvelle image, retour à 1 au load complete.
 *
 * Si la nouvelle URL échoue à charger (404, CORS, image invalide) → rollback
 * sur l'URL précédente pour éviter que le browser affiche l'alt text nu.
 */
function fadeSwapMainImage(el: HTMLElement, url: string): void {
  const current = extractCurrentImageUrl(el);
  if (current === url) return;
  el.style.opacity = "0.5";
  const restore = (): void => {
    el.style.opacity = "";
  };
  if (el instanceof HTMLImageElement) {
    const onLoad = (): void => {
      restore();
      cleanup();
    };
    const onError = (): void => {
      // Rollback : remet l'URL précédente si la nouvelle est cassée.
      if (current) el.src = current;
      restore();
      cleanup();
    };
    const cleanup = (): void => {
      el.removeEventListener("load", onLoad);
      el.removeEventListener("error", onError);
    };
    el.addEventListener("load", onLoad, { once: true });
    el.addEventListener("error", onError, { once: true });
    el.src = url;
    // Fallback timeout si l'image est déjà en cache et pas de load event.
    setTimeout(restore, 300);
  } else {
    el.style.backgroundImage = `url(${JSON.stringify(url)})`;
    setTimeout(restore, 180);
  }
}

function extractCurrentImageUrl(el: HTMLElement): string | null {
  if (el instanceof HTMLImageElement) {
    return el.src || null;
  }
  const bg = el.style.backgroundImage || getComputedStyle(el).backgroundImage;
  const m = bg.match(/url\(["']?([^"')]+)["']?\)/);
  return m ? m[1]! : null;
}

function swapMainImage(el: HTMLElement, url: string): void {
  if (el instanceof HTMLImageElement) {
    el.src = url;
  } else {
    el.style.backgroundImage = `url(${JSON.stringify(url)})`;
  }
}

// ---------------------------------------------------------------------------
//  Webflow slider hydration
// ---------------------------------------------------------------------------

/**
 * Peuple un `.w-slider[product="images"]` avec N slides (une par image
 * produit) + thumbnails, et installe notre propre navigation (flèches +
 * thumbnails → swap de slide active).
 *
 * Pourquoi pas Webflow ? Webflow slider n'a pas d'API publique pour
 * re-initialiser sur contenu dynamique. `destroy()+ready()` ne régénère
 * pas correctement les transforms sur slides ajoutées après boot — résultat :
 * les flèches changent l'état interne mais les slides ne bougent pas
 * visuellement. On court-circuite Webflow : on désactive son init (retrait
 * de la class `w-slider`) et on gère nous-même l'active slide via `display`.
 *
 * Idempotent via `data-lmdr-images-count`.
 */
function hydrateWebflowSlider(
  slider: Element,
  images: readonly ShopifyImage[],
): void {
  if (images.length === 0) return;

  const mask = slider.querySelector<HTMLElement>(".w-slider-mask");
  if (!mask) return;

  const templateSlide = mask.querySelector<HTMLElement>(
    ".w-slide:not(.w-clone):not(.w-slider-aria-label)",
  );
  if (!templateSlide) return;

  const lastCount = slider.getAttribute("data-lmdr-images-count");
  if (lastCount === String(images.length)) return;

  // 1. Nettoie tout ce que Webflow a injecté dans le mask (slides originales
  //    + clones pour infinite + aria-labels + anciens wrappers à nous).
  mask
    .querySelectorAll(".w-slide, .w-slider-aria-label, [data-lmdr-slides-wrap]")
    .forEach((s) => s.remove());

  // 2. Crée un wrapper flex qui translate-X pour l'animation smooth.
  //    Les slides sont en ligne (flex: 0 0 100%), le wrapper bouge.
  const wrap = document.createElement("div");
  wrap.setAttribute("data-lmdr-slides-wrap", "");
  mask.appendChild(wrap);

  // 3. Repose N slides propres, une par image, dans le wrapper.
  images.forEach((img, i) => {
    const clone = templateSlide.cloneNode(true) as HTMLElement;
    clone.classList.remove("w-clone");
    clone.removeAttribute("aria-hidden");
    clone.removeAttribute("tabindex");
    clone.removeAttribute("role");
    clone.style.removeProperty("transform");
    clone.style.removeProperty("transition");
    clone.style.removeProperty("opacity");
    clone.setAttribute("data-lmdr-slide-index", String(i));
    applySlideImage(clone, img);
    wrap.appendChild(clone);
  });

  // 3. Désactive l'init Webflow sur CE slider — retire la class `w-slider`
  //    pour que Webflow l'ignore si quelqu'un rappelle `ready()` plus tard.
  //    On garde les classes de style visuel (`slider`).
  slider.classList.remove("w-slider");

  // 4. Installe notre navigation arrow + swipe + thumbnails.
  attachSliderArrowNav(slider);
  attachSliderSwipe(slider);

  slider.setAttribute("data-lmdr-images-count", String(images.length));
  slider.setAttribute("data-lmdr-slide-active", "0");
  // Reset position du wrapper au 1er slide.
  wrap.style.transform = "translateX(0%)";

  // 5. Thumbnails (si container présent dans le même smootify-product).
  hydrateThumbnails(slider, images);

  // 6. Ré-init du module lightbox Webflow — chaque <a class="w-lightbox"> des
  //    clones a maintenant son propre JSON config avec la bonne URL. Le module
  //    doit re-scanner le DOM pour binder les click handlers.
  reinitWebflowLightbox();

  // 7. Magnifier zoom au hover desktop — remplace le clic sur l'icône par
  //    un effet loupe qui suit la souris.
  attachMagnifierZoom(slider);
}

/**
 * Demande à Webflow de re-scanner les `<a class="w-lightbox">` pour y
 * attacher ses click handlers.
 */
function reinitWebflowLightbox(): void {
  interface WebflowLike {
    require?: (name: string) => unknown;
  }
  interface LightboxModule {
    ready?: () => void;
  }
  try {
    const wf = (window as unknown as { Webflow?: WebflowLike }).Webflow;
    const mod = wf?.require?.("lightbox") as LightboxModule | undefined;
    mod?.ready?.();
  } catch {
    /* noop */
  }
}

/**
 * Installe les click handlers sur les flèches gauche/droite d'un slider.
 * Chaque click met à jour `data-lmdr-slide-active`, affiche le bon slide
 * (display:block) et cache les autres.
 */
function attachSliderArrowNav(slider: Element): void {
  if (slider.hasAttribute("data-lmdr-arrows-bound")) return;
  slider.setAttribute("data-lmdr-arrows-bound", "true");

  const leftArrow = slider.querySelector<HTMLElement>(".w-slider-arrow-left");
  const rightArrow = slider.querySelector<HTMLElement>(".w-slider-arrow-right");

  const goto = (delta: number): void => {
    const total = Number.parseInt(
      slider.getAttribute("data-lmdr-images-count") ?? "0",
      10,
    );
    if (total <= 0) return;
    const current = Number.parseInt(
      slider.getAttribute("data-lmdr-slide-active") ?? "0",
      10,
    );
    const next = (current + delta + total) % total;
    setActiveSlide(slider, next);
  };

  leftArrow?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    goto(-1);
  });
  rightArrow?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    goto(1);
  });

  // Écoute les demandes externes d'activation de slide (ex: click variante).
  slider.addEventListener("lmdr:set-active-slide", (ev) => {
    const detail = (ev as CustomEvent<{ idx: number }>).detail;
    if (typeof detail?.idx === "number") {
      setActiveSlide(slider, detail.idx);
    }
  });
}

function attachSliderSwipe(slider: Element): void {
  if (slider.hasAttribute("data-lmdr-swipe-bound")) return;

  const mask = slider.querySelector<HTMLElement>(".w-slider-mask");
  if (!mask) return;

  slider.setAttribute("data-lmdr-swipe-bound", "true");
  mask.style.touchAction = "pan-y";

  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;

  const reset = (): void => {
    pointerId = null;
    startX = 0;
    startY = 0;
  };

  const finish = (ev: PointerEvent): void => {
    if (pointerId !== ev.pointerId) return;

    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;
    reset();

    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.25) return;

    const total = Number.parseInt(
      slider.getAttribute("data-lmdr-images-count") ?? "0",
      10,
    );
    if (total <= 1) return;

    const current = Number.parseInt(
      slider.getAttribute("data-lmdr-slide-active") ?? "0",
      10,
    );
    const next = dx < 0 ? current + 1 : current - 1;
    setActiveSlide(slider, (next + total) % total);
    ev.preventDefault();
  };

  mask.addEventListener("pointerdown", (ev) => {
    if (ev.pointerType === "mouse") return;
    pointerId = ev.pointerId;
    startX = ev.clientX;
    startY = ev.clientY;
    mask.setPointerCapture?.(ev.pointerId);
  });
  mask.addEventListener("pointerup", finish);
  mask.addEventListener("pointercancel", () => reset());
}

/**
 * Affiche le slide `idx` via transform:translateX sur le wrapper.
 * L'animation CSS (transition 350ms ease-in-out) gère le glissement smooth.
 * Met aussi à jour les thumbnails pour refléter l'active.
 */
function setActiveSlide(slider: Element, idx: number): void {
  slider.setAttribute("data-lmdr-slide-active", String(idx));
  const wrap = slider.querySelector<HTMLElement>("[data-lmdr-slides-wrap]");
  if (wrap) {
    wrap.style.transform = `translateX(-${idx * 100}%)`;
  }
  const host = slider.closest("smootify-product") ?? slider.parentElement;
  host?.querySelectorAll<HTMLElement>("[data-lmdr-thumb-index]").forEach((t) => {
    const i = Number.parseInt(t.getAttribute("data-lmdr-thumb-index") ?? "-1", 10);
    t.classList.toggle("active", i === idx);
  });
}

/**
 * Remplace l'image d'un slide — supporte 3 modes :
 *   - background-image CSS inline
 *   - <img> enfant
 *   - Webflow lightbox (<script class="w-json">) : met à jour la config JSON
 *     pour que le lightbox ouvre LA bonne image, pas celle du template.
 */
function applySlideImage(slide: Element, image: ShopifyImage): void {
  if (slide instanceof HTMLElement) {
    const bg = slide.getAttribute("style") ?? "";
    if (bg.includes("background-image")) {
      slide.style.backgroundImage = `url(${JSON.stringify(image.url)})`;
    }
    // Stocke l'URL sur le slide pour le magnifier zoom hover.
    slide.setAttribute("data-lmdr-zoom-src", image.url);
  }
  const img = slide.querySelector<HTMLImageElement>("img");
  if (img) {
    // Slide produit (page detail) = ~600-800px max width → resize 800
    img.src = shopifyResize(image.url, 800);
    if (image.altText) img.alt = image.altText;
    img.removeAttribute("width");
    img.removeAttribute("height");
    img.removeAttribute("srcset");
  }
  // Webflow lightbox : le <a class="w-lightbox"> contient un
  // <script class="w-json"> avec la config `{items: [{url, type, ...}]}`.
  // Si on ne le met pas à jour, chaque clone ouvre la MÊME image (celle
  // du template). On réécrit le script avec l'URL du clone courant.
  const lightboxLink = slide.querySelector<HTMLAnchorElement>("a.w-lightbox");
  if (lightboxLink) {
    const jsonScript = lightboxLink.querySelector<HTMLScriptElement>(
      "script.w-json",
    );
    if (jsonScript) {
      try {
        const existing = JSON.parse(jsonScript.textContent ?? "{}") as {
          items?: Array<{ url: string; type?: string }>;
          group?: string;
        };
        if (Array.isArray(existing.items) && existing.items.length > 0) {
          existing.items[0]!.url = image.url;
        } else {
          existing.items = [{ url: image.url, type: "image" }];
        }
        jsonScript.textContent = JSON.stringify(existing);
      } catch {
        // Fallback : réécrit from scratch
        jsonScript.textContent = JSON.stringify({
          items: [{ url: image.url, type: "image" }],
        });
      }
    }
  }
}

/**
 * Clone les thumbnails pour couvrir chaque image du produit. Le template
 * Webflow pose généralement `product="images"` sur le premier `<a>` du
 * `.thumbnails-container`. On les remplace tous par N clones hydratés.
 */
function hydrateThumbnails(
  slider: Element,
  images: readonly ShopifyImage[],
): void {
  // Le container thumbnails est frère du slider (ou cousin) — on remonte au
  // smootify-product ancestor pour le trouver, plus robuste.
  const host = slider.closest("smootify-product") ?? slider.parentElement;
  if (!host) return;
  const container = host.querySelector<HTMLElement>(".thumbnails-container");
  if (!container) return;

  const lastCount = container.getAttribute("data-lmdr-thumbs-count");
  if (lastCount === String(images.length)) return;

  // Cherche un template : 1er enfant avec [product="images"] ou 1er <a>.
  const template =
    container.querySelector<HTMLElement>("[product='images']") ??
    container.querySelector<HTMLElement>("a, .thumbnail, li");
  if (!template) return;

  // Supprime tous les enfants du container.
  const parent = template.parentElement ?? container;
  Array.from(parent.children).forEach((c) => c.remove());

  // Clone pour chaque image.
  images.forEach((img, i) => {
    const clone = template.cloneNode(true) as HTMLElement;
    const tImg = clone.querySelector<HTMLImageElement>("img");
    if (tImg) {
      // Thumbnail page produit = ~80-100px max → resize 200
      tImg.src = shopifyResize(img.url, 200);
      if (img.altText) tImg.alt = img.altText;
      tImg.removeAttribute("width");
      tImg.removeAttribute("height");
      tImg.removeAttribute("srcset");
      if (!tImg.hasAttribute("loading")) tImg.setAttribute("loading", "lazy");
    }
    // CRITIQUE : la thumbnail Webflow contient un `<a class="w-lightbox">`
    // avec `<script class="w-json">` qui a la config items=placeholder.
    // Sans rewrite, cliquer la thumb ouvre la lightbox avec un placeholder
    // SVG au lieu de la vraie image. On reecrit le JSON pour pointer vers
    // l image reelle de cette thumb. items=[image actuelle] (la lightbox
    // ouvrira juste cette image, prev/next via les arrows si meme group).
    //
    // Bug fixe : `clone` PEUT etre lui-meme le `<a class="w-lightbox">`
    // (thumbnail structure Webflow = anchor direct, pas wrapper+anchor).
    // querySelector ne matche pas l element lui-meme, d ou le fallback
    // via matches().
    const lbLink =
      (clone.matches?.("a.w-lightbox") ? (clone as HTMLAnchorElement) : null) ??
      clone.querySelector<HTMLAnchorElement>("a.w-lightbox");
    if (lbLink) {
      const jsonScript = lbLink.querySelector<HTMLScriptElement>("script.w-json");
      if (jsonScript) {
        try {
          const existing = JSON.parse(jsonScript.textContent ?? "{}") as {
            items?: Array<{ url: string; type?: string }>;
            group?: string;
          };
          const newItems = [{ url: img.url, type: "image" }];
          existing.items = newItems;
          // Group commun pour permettre prev/next entre toutes les thumbs.
          if (!existing.group) existing.group = "lmdr-product-thumbs";
          jsonScript.textContent = JSON.stringify(existing);
        } catch {
          jsonScript.textContent = JSON.stringify({
            items: [{ url: img.url, type: "image" }],
            group: "lmdr-product-thumbs",
          });
        }
      }
    }
    // Marque la 1ère comme active (convention Webflow : classe `active`).
    clone.classList.toggle("active", i === 0);
    // Stocke l'index pour le click-to-slide (implémenté plus bas).
    clone.setAttribute("data-lmdr-thumb-index", String(i));
    parent.appendChild(clone);
  });

  container.setAttribute("data-lmdr-thumbs-count", String(images.length));

  // Click-to-slide : relie thumbnails au slider parent.
  attachThumbnailClicks(container, slider);
}

// ---------------------------------------------------------------------------
//  Magnifier zoom (desktop hover)
// ---------------------------------------------------------------------------

/**
 * Installe un effet "loupe" au hover sur chaque slide : un cercle 180×180px
 * qui suit la souris et affiche une vue zoomée (×2.5) de la zone pointée.
 *
 * Activé UNIQUEMENT sur :
 *   - viewports desktop (>= 992px, seuil Webflow)
 *   - devices sans touch (pointer:fine) — évite de gêner mobile/tablet
 *
 * L'icône "zoom" Webflow (flèche) est cachée pendant le hover pour laisser
 * place à la loupe. Le clic reste fonctionnel (ouvre le lightbox Webflow
 * via bubble — on ne preventDefault PAS).
 */
function attachMagnifierZoom(slider: Element): void {
  if (slider.hasAttribute("data-lmdr-zoom-bound")) return;
  slider.setAttribute("data-lmdr-zoom-bound", "true");

  // Check au runtime : desktop + pointer fine
  const mq = window.matchMedia("(min-width: 992px) and (pointer: fine)");
  if (!mq.matches) {
    // Ré-essaie si le viewport grandit (ex: rotation tablette, resize window).
    mq.addEventListener?.("change", () => {
      if (mq.matches && !slider.hasAttribute("data-lmdr-zoom-active")) {
        installMagnifierOnce(slider);
      }
    });
    return;
  }
  installMagnifierOnce(slider);
}

function installMagnifierOnce(slider: Element): void {
  if (slider.hasAttribute("data-lmdr-zoom-active")) return;
  slider.setAttribute("data-lmdr-zoom-active", "true");

  const mask = slider.querySelector<HTMLElement>(".w-slider-mask");
  if (!mask) return;
  const maskEl = mask;

  // Crée la loupe une seule fois, l'attache au mask.
  const lens = document.createElement("div");
  lens.setAttribute("data-lmdr-zoom-lens", "");
  lens.setAttribute(
    "style",
    [
      "position:absolute",
      "pointer-events:none",
      "width:180px",
      "height:180px",
      "border-radius:50%",
      "border:2px solid rgba(255,255,255,0.9)",
      "box-shadow:0 2px 12px rgba(0,0,0,0.3)",
      "background-repeat:no-repeat",
      "background-color:#fff",
      "opacity:0",
      "transition:opacity 150ms ease-out",
      "z-index:10",
      "transform:translate(-50%,-50%)",
      "top:0",
      "left:0",
    ].join(";"),
  );
  // S'assure que le mask est bien position:relative pour l'ancrage absolu.
  if (getComputedStyle(maskEl).position === "static") {
    maskEl.style.position = "relative";
  }
  maskEl.appendChild(lens);

  const ZOOM_FACTOR = 2.5;

  function onMove(ev: MouseEvent): void {
    const target = ev.target as Element | null;
    // On laisse l'utilisateur interagir avec les contrôles superposés
    // (wishlist, zoom lightbox, arrows) en masquant la loupe au-dessus d'eux.
    if (
      target?.closest(
        "wishlist-toggle, .wishlist_toggle, .icon-wishlist, .lightbox-link, .w-lightbox, button, .w-slider-arrow-left, .w-slider-arrow-right",
      )
    ) {
      lens.style.opacity = "0";
      return;
    }
    const slide = target?.closest?.("[data-lmdr-slide-index]");
    if (!slide || !(slide instanceof HTMLElement)) {
      lens.style.opacity = "0";
      return;
    }
    const zoomSrc = slide.getAttribute("data-lmdr-zoom-src");
    if (!zoomSrc) {
      lens.style.opacity = "0";
      return;
    }

    const slideRect = slide.getBoundingClientRect();
    const maskRect = maskEl.getBoundingClientRect();

    // Position relative au mask (parent absolute).
    const relX = ev.clientX - maskRect.left;
    const relY = ev.clientY - maskRect.top;

    lens.style.left = `${relX}px`;
    lens.style.top = `${relY}px`;
    lens.style.opacity = "1";

    // Position du background : pour une loupe ×N, l'image de fond a une taille
    // N × slide.size. On aligne le centre de la loupe avec le point pointé.
    const bgW = slideRect.width * ZOOM_FACTOR;
    const bgH = slideRect.height * ZOOM_FACTOR;
    // Position du pointeur DANS la slide (0..W, 0..H).
    const slideX = ev.clientX - slideRect.left;
    const slideY = ev.clientY - slideRect.top;
    const ratioX = slideX / slideRect.width;
    const ratioY = slideY / slideRect.height;
    // background-position : origine (0,0) de la vue loupe — on veut que le
    // pixel à (ratioX*bgW, ratioY*bgH) tombe au centre de la lens (90,90).
    const bgPosX = -(ratioX * bgW - 90);
    const bgPosY = -(ratioY * bgH - 90);

    lens.style.backgroundImage = `url(${JSON.stringify(zoomSrc)})`;
    lens.style.backgroundSize = `${bgW}px ${bgH}px`;
    lens.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
  }

  function onLeave(): void {
    lens.style.opacity = "0";
  }

  maskEl.addEventListener("mousemove", onMove);
  maskEl.addEventListener("mouseleave", onLeave);

  // NE PAS cacher l'icône zoom flèche — elle reste visible. Le onMove
  // ci-dessus cache la loupe quand la souris est sur l'icône, ce qui
  // permet de cliquer dessus pour ouvrir le lightbox fullscreen.
}

/**
 * Attache les click handlers sur les thumbnails pour naviguer dans le slider
 * via `setActiveSlide` (pas Webflow).
 */
function attachThumbnailClicks(container: Element, slider: Element): void {
  if (container.hasAttribute("data-lmdr-thumbs-click-bound")) return;
  container.setAttribute("data-lmdr-thumbs-click-bound", "true");

  container.addEventListener("click", (ev) => {
    const target = (ev.target as Element)?.closest?.("[data-lmdr-thumb-index]");
    if (!target) return;
    ev.preventDefault();
    ev.stopPropagation();
    const idx = Number.parseInt(
      target.getAttribute("data-lmdr-thumb-index") ?? "0",
      10,
    );
    setActiveSlide(slider, idx);
  });
}
